/**
 * Имя поставщика с меню переходов — одно на всю систему (разметка владельца
 * 25.09.26). Спека: `docs/specs/2026-09-25-supplier-card-design.md`.
 *
 * Меню — в ПОРТАЛЕ с `position: fixed`: имя стоит в таблицах со своей
 * прокруткой и `overflow: hidden`, и меню внутри них обрезалось бы краем.
 * Щелчок и клавиши по имени НЕ ВСПЛЫВАЮТ: в строке таблицы они раскрывали бы
 * строку или уводили в заказ. Пункты — настоящие ссылки: Ctrl+щелчок и
 * средняя кнопка открывают вкладку.
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";

import { ORDERS_VIEW, PAYMENTS_VIEW, useCan } from "@/lib/access";
import { useEscapeLayer } from "@/lib/escapeStack";
import { type SupplierExtraItem, SupplierMenuExtras, supplierMenuItems } from "@/lib/supplierMenu";
import { usePopover } from "@/lib/usePopover";

const ШИРИНА = 220;
const ОТСТУП = 8;
const СТРОКА =
  "flex w-full items-center px-3 py-1 text-left text-[12.5px] text-fg hover:bg-bg-hover focus-visible:bg-bg-hover focus-visible:outline-none";

type Место = { left: number; top?: number; bottom?: number };

/** Под именем, а если снизу не хватает места — над ним; по ширине — в окно. */
function место(кнопка: HTMLElement, пунктов: number): Место {
  const b = кнопка.getBoundingClientRect();
  const высота = пунктов * 26 + 12;
  const left = Math.max(ОТСТУП, Math.min(b.left, window.innerWidth - ШИРИНА - ОТСТУП));
  if (b.bottom + высота + ОТСТУП > window.innerHeight && b.top - высота > ОТСТУП) {
    return { left, bottom: window.innerHeight - b.top + 2 };
  }
  return { left, top: b.bottom + 2 };
}

export function SupplierName({
  id,
  name,
  title,
  className = "",
  withCard = true,
  children,
}: {
  id: number | null | undefined;
  name: string | null | undefined;
  /** Подсказка — обычно полное имя поставщика. */
  title?: string | null | undefined;
  className?: string;
  /** На самой карточке пункт «Карточка поставщика» не нужен. */
  withCard?: boolean;
  /** Что показать вместо имени — если имя оформлено особо. */
  children?: ReactNode;
}) {
  const orders = useCan(ORDERS_VIEW);
  const payments = useCan(PAYMENTS_VIEW);
  const [open, setOpen] = useState(false);
  const [где, setГде] = useState<Место>({ left: 0, top: 0 });
  const кнопка = useRef<HTMLButtonElement>(null);
  const меню = useRef<HTMLDivElement>(null);
  const [старое, setСтарое] = useState(false);
  const подпись = children ?? name ?? (id ? `поставщик ${id}` : "без поставщика");

  const items = id ? supplierMenuItems(id, { orders, payments }, withCard) : [];
  // Пункты экрана (`SupplierMenuExtras`): есть — общие уходят в «Старое».
  const пунктыЭкрана = useContext(SupplierMenuExtras);
  const extras: SupplierExtraItem[] = id && open && пунктыЭкрана ? пунктыЭкрана(id) : [];
  const свёрнуто = extras.length > 0;
  const строк = свёрнуто ? extras.length + 1 + (старое ? items.length : 0) : items.length;

  useLayoutEffect(() => {
    if (open && кнопка.current) setГде(место(кнопка.current, строк));
  }, [open, строк]);

  // Esc — через стопку слоёв, и только пока меню открыто: имя стоит и внутри
  // выезжающих панелей, а свой слушатель на `document` глушился бы слоем
  // панели — панель закрывалась, меню висело.
  useEscapeLayer(() => {
    setOpen(false);
    кнопка.current?.focus();
  }, open);

  // Клик мимо — хуком (меню порталом: «внутри» — и оно, и кнопка); Esc — стопкой
  // слоёв выше, а не слушателем документа.
  usePopover(open, () => setOpen(false), [меню, кнопка], { escape: false });

  useEffect(() => {
    if (!open) return;
    const закрыть = () => setOpen(false);
    // Прокрутка любой области уводит имя из-под меню — закрываем, а не догоняем.
    const прокрутка = (e: Event) => {
      if (!меню.current?.contains(e.target as Node)) закрыть();
    };
    window.addEventListener("scroll", прокрутка, true);
    window.addEventListener("resize", закрыть);
    return () => {
      window.removeEventListener("scroll", прокрутка, true);
      window.removeEventListener("resize", закрыть);
    };
  }, [open]);

  if (!id) {
    return (
      <span className={className} title={title ?? undefined}>
        {подпись}
      </span>
    );
  }

  const шаг = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const пункты = [...(меню.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const сейчас = пункты.indexOf(document.activeElement as HTMLElement);
    const дальше = e.key === "ArrowDown" ? сейчас + 1 : сейчас - 1;
    пункты[(дальше + пункты.length) % пункты.length]?.focus();
  };

  return (
    <>
      <button
        ref={кнопка}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        title={title ?? undefined}
        className={`max-w-full cursor-pointer truncate text-left underline-offset-2 hover:underline ${className}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen((was) => !was);
          setСтарое(false);
        }}
        onKeyDown={(e) => {
          // Enter и пробел — свои у кнопки; наружу (в строку) не пускаем.
          e.stopPropagation();
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => меню.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus());
          }
        }}
      >
        {подпись}
      </button>
      {open
        ? createPortal(
            <div
              ref={меню}
              role="menu"
              aria-label={`Поставщик ${name ?? id}`}
              style={{ width: ШИРИНА, ...где }}
              className="fixed z-[60] rounded-[var(--radius-ui)] border border-border bg-bg-elevated py-1 shadow-lg"
              onKeyDown={шаг}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="truncate px-3 pt-0.5 pb-1 text-[11px] text-fg-muted">{name ?? `поставщик ${id}`}</div>
              {extras.map((item) =>
                item.disabled ? (
                  <span
                    key={item.key}
                    role="menuitem"
                    tabIndex={-1}
                    aria-disabled="true"
                    title={item.disabled}
                    className={`${СТРОКА} cursor-not-allowed text-fg-faint hover:bg-transparent`}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    key={item.key}
                    role="menuitem"
                    to={item.href}
                    {...(item.sameTab ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                    className={СТРОКА}
                    onClick={() => setOpen(false)}
                  >
                    {item.label}
                  </Link>
                ),
              )}
              {свёрнуто ? (
                <button
                  type="button"
                  role="menuitem"
                  aria-expanded={старое}
                  className={`${СТРОКА} gap-1 text-fg-muted`}
                  onClick={() => setСтарое((was) => !was)}
                >
                  Старое
                  {старое ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </button>
              ) : null}
              {(!свёрнуто || старое) &&
                items.map((item) => (
                  <Link
                    key={item.key}
                    role="menuitem"
                    to={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={свёрнуто ? `${СТРОКА} pl-6` : СТРОКА}
                    onClick={() => setOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
